import React, { useEffect, useRef, useState } from 'react';

import { FieldArray, Formik, useFormikContext } from 'formik';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import * as Yup from 'yup';

import AutoAllocationModal from './AutoAllocationModal';
import MilestoneRow from './MilestoneRow';

import { formatMoneyWithLocale, toFullPrecisionString, truncateDecimals } from 'Helpers/formatService';
import { isSingleScreenCommitSection, reconcileMilestoneValue } from 'Helpers/procurementUtils';

import Button from 'CommonComponents/Button';
import { DragAndDrop } from 'CommonComponents/DragAndDrop';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

const CASH_MILESTONE_TYPE = 'fixed/cash';
const DEFAULT_MILESTONE = {
    id: null,
    name: '',
    date: '',
    description: null,
    perc: 0,
    percOriginal: '0',
    value: 0,
    valueOriginal: '0',
    type: CASH_MILESTONE_TYPE,
};
const MilestoneSectionSchema = Yup.object().shape({
    dates: Yup.array()
        .of(
            Yup.object().shape({
                date: Yup.date().required($t('This value is required')),
                name: Yup.string().nullable(),
                description: Yup.string().nullable(),
                perc: Yup.number()
                    .min(0, $t('Percentage must be at least 0'))
                    .max(100, $t('Percentage must be 100 or less'))
                    .required($t('This value is required')),
            }),
        )
        .test('total-percentage', $t('Total percentage must equal 100'), (dates) => {
            const total = (dates || []).reduce((sum, milestone) => {
                const perc = parseFloat(milestone?.percOriginal ?? milestone?.perc ?? 0);
                return sum + (isNaN(perc) ? 0 : perc);
            }, 0);
            return Math.abs(total - 100) <= 0.01;
        }),
});

export default function MilestoneSectionForm({ showCancelButton = true, totalValue = 0, isSliderAllowed = true, ...props }) {
    const [totalPercentage, setTotalPercentage] = useState(100);
    const [totalMilestoneValue, setTotalMilestoneValue] = useState(totalValue);
    const [initialMilestones, setInitialMilestones] = useState([]);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [allocationMode, setAllocationMode] = useState('manual');
    const [showAutoModal, setShowAutoModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const isTotalPercentageValid = Math.abs(totalPercentage - 100) <= 0.01;
    const isTotalValueValid = Math.abs(totalMilestoneValue - totalValue) <= 0.01;
    const hasAvailablePercentage = totalPercentage < 99.99;
    // Direct Commitment and Commit Ticketing are both single-screen flows: no per-section
    // Confirm button, validity + autosave state lifted to the parent's single commit action.
    const isSingleScreenCommit = isSingleScreenCommitSection(props.parentSection);
    const autoSaveTimerRef = useRef(null);

    const toast = useToast();

    // In a single-screen commit flow, lift this section's percentage validity up to the
    // parent so its single commit button can be gated on every present milestone section
    // equalling 100%.
    useEffect(() => {
        if (isSingleScreenCommit && props.onValidityChange) {
            props.onValidityChange(props.section, isTotalPercentageValid);
        }
    }, [isSingleScreenCommit, props.section, isTotalPercentageValid]);

    // Tell the parent which sections are present (cash / VIK) so it only gates on
    // sections that actually exist.
    useEffect(() => {
        if (isSingleScreenCommit && props.onSectionPresent) {
            props.onSectionPresent(props.section);
        }
    }, [isSingleScreenCommit, props.section]);

    useEffect(() => {
        setIsConfirmed(getDefaultIsConfirmed());
    }, []);

    // On unmount, cancel any pending autosave debounce AND clear this section's saving flag in
    // the parent. Without this, a section unmounting mid-debounce (e.g. an edit-modal save bumps
    // reloadSignal → fetchData → setSections drops/re-derives a section) leaves the .finally()
    // that clears the flag never running, so the parent's Direct Commit button stays disabled.
    useEffect(
        () => () => {
            clearTimeout(autoSaveTimerRef.current);
            reportSaving(false);
        },
        [],
    );

    useEffect(() => {
        if (!props.milestones || props.milestones.length === 0) {
            setInitialMilestones([]);
            return;
        }

        // Pass 1: compute all fields that other milestones depend on (percOriginal, perc, value)
        const firstPass = props.milestones.map((milestone) => {
            const percentageValue = parseFloat(milestone.percentage ?? DEFAULT_MILESTONE.perc);
            const safePercentage = isNaN(percentageValue) ? 0 : percentageValue;
            const hasExplicitValue = milestone.value !== undefined && milestone.value !== null;
            const explicitValue = hasExplicitValue ? parseFloat(milestone.value) : null;
            // Reconciled against totalValue x percentage rather than trusted outright. This
            // reinitialises the form from the values the parent last received, so an explicit
            // value captured before the committing amount moved would otherwise be reinstated
            // here on top of an already-corrected row.
            const originalValue = reconcileMilestoneValue(isNaN(explicitValue) ? null : explicitValue, totalValue, safePercentage);
            const truncatedValue = truncateDecimals(originalValue, 2);
            const percOriginalString = toFullPrecisionString(safePercentage);
            const valueOriginalString = toFullPrecisionString(originalValue);

            return {
                ...milestone,
                description: milestone.description ?? DEFAULT_MILESTONE.description,
                name: milestone.name ?? DEFAULT_MILESTONE.name,
                date: milestone.date ?? DEFAULT_MILESTONE.date,
                perc: truncateDecimals(safePercentage, 2),
                percOriginal: percOriginalString,
                valueOriginal: valueOriginalString,
                value: truncatedValue,
                // The SECTION's type, not DEFAULT_MILESTONE's cash. This is the one place an untyped
                // row can acquire a type, and a cash-typed row inside the VIK section survives
                // updateSectionMilestones()'s `!m.type.includes('vik')` filter in MilestonesEdit, so
                // it migrates into the cash bucket and renders in the wrong table.
                type: milestone.type ?? `fixed/${props.section}`,
                canDeleteMilestones: props.canDeleteMilestones,
            };
        });

        // Pass 2: compute uniqueKey and maxPercentage using the correct percOriginal values so
        // FormObserver finds nothing to update on mount and triggers no initial cascade.
        const withDates = { dates: firstPass };
        setInitialMilestones(
            firstPass.map((milestone, index) => ({
                ...milestone,
                uniqueKey: getUniqueKeyBasedOnIndex(withDates, index),
                maxPercentage: getMaxPercentage(withDates, index) ?? DEFAULT_MILESTONE.perc,
            })),
        );
    }, [props.milestones]);

    useEffect(() => {
        if (props.section === 'cash' ? props.setCashMilestonesConfirmed : props.setVikMilestonesConfirmed) {
            setIsConfirmed(props.section === 'cash' ? props.cashMilestonesConfirmed : props.vikMilestonesConfirmed);
        } else {
            setIsConfirmed(props.milestonesConfirmed);
        }
    }, [props.milestonesConfirmed, props.cashMilestonesConfirmed, props.vikMilestonesConfirmed]);

    useEffect(() => {
        setTotalMilestoneValue(totalValue);
    }, [totalValue]);

    const getMaxPercentage = (values, selectedIndex) => {
        const usedPercentage =
            values.dates?.reduce((sum, milestone, index) => {
                if (index === selectedIndex) {
                    return sum;
                }
                const perc = parseFloat(milestone.percOriginal ?? milestone.perc ?? 0);
                return sum + (isNaN(perc) ? 0 : perc);
            }, 0) ?? 0;
        const remaining = 100 - usedPercentage;
        return remaining > 0 ? remaining : 0;
    };

    const getDefaultIsConfirmed = () => {
        if (props.section === 'cash' ? props.setCashMilestonesConfirmed : props.setVikMilestonesConfirmed) {
            return props.section === 'cash' ? props.cashMilestonesConfirmed : props.vikMilestonesConfirmed;
        } else {
            return props.milestonesConfirmed;
        }
    };

    const getDefaultMilestone = (values) => {
        // Typed for the section it is being added to, NOT DEFAULT_MILESTONE's cash. A cash-typed row
        // added inside the VIK section survives updateSectionMilestones()'s
        // `!m.type.includes('vik')` filter in MilestonesEdit, so it migrates into the cash bucket and
        // renders in the cash table with an empty date — leaving VIK under 100% with no way to fix
        // it, and 422-ing the cash section's autosave from then on. Revenue and Ticketing are
        // cash-only so this is a no-op there.
        let milestone = { ...DEFAULT_MILESTONE, type: `fixed/${props.section}` };
        milestone.maxPercentage = 100 - totalPercentage;
        milestone.canDeleteMilestones = true;
        milestone.uniqueKey = getUniqueKeyBasedOnIndex(values, (values?.dates?.length || 0) + 1);

        return milestone;
    };

    const valuesListener = (percentage, value) => {
        setTotalPercentage(percentage);
        setTotalMilestoneValue(value);
        if (props.setMilestonesConfirmed) {
            if (props.section === 'cash' ? props.setCashMilestonesConfirmed : props.setVikMilestonesConfirmed) {
                props.section === 'cash' ? props.setCashMilestonesConfirmed(false) : props.setVikMilestonesConfirmed(false);
            }
            props.setMilestonesConfirmed(false);
            setIsConfirmed(false);
        }
    };

    const redirectBack = () => {
        window.location.href = props.redirectRoute;
    };

    const showActionButtons = () => {
        return props.canEdit && (props.parentSection === 'expense' || (props.parentSection !== 'expense' && !isConfirmed));
    };

    const getUniqueKeyBasedOnIndex = (values, index) => {
        if ((values?.dates?.length || 0) === 0) {
            return 0;
        }
        return values?.dates?.length + index;
    };

    // Check if the milestones are equal by comparing the id, name, date, description, type, percentage, and value
    const areMilestonesEqual = (current, next) => {
        if (!current && !next) return true;
        if (!current || !next || current.length !== next.length) return false;
        return next.every((m, i) => {
            const c = current[i];
            return (
                c?.id === m?.id &&
                c?.name === m?.name &&
                c?.date === m?.date &&
                c?.description === m?.description &&
                c?.type === m?.type &&
                Math.abs((parseFloat(c?.percentage) || 0) - (parseFloat(m?.percentage) || 0)) <= 0.01 &&
                Math.abs((parseFloat(c?.value) || 0) - (parseFloat(m?.value) || 0)) <= 0.01
            );
        });
    };

    // Update the milestones for a specific section in the parent component to keep track of the changes happening in milestones form
    // This is used to preserve the existing milestones when the user makes any changes in the milestones form
    const handleMilestonesChange = (normalizedMilestones) => {
        if (props.setMilestones && normalizedMilestones && !areMilestonesEqual(props.milestones, normalizedMilestones)) {
            props.setMilestones(props.section, normalizedMilestones);
        }

        // Single-screen commit: there is no per-section Confirm button — auto-save the
        // draft whenever the section is valid (percentages total 100%). Debounced so
        // we don't post on every keystroke, and silent (no toast).
        if (isSingleScreenCommit && normalizedMilestones && normalizedMilestones.length > 0) {
            const total = normalizedMilestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
            const valid = Math.abs(total - 100) <= 0.01;

            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            if (valid) {
                // Mark this section's autosave as pending immediately so the parent's
                // Direct Commit button is gated for the whole debounce-plus-request window,
                // closing the race between a fast commit click and an unpersisted draft.
                reportSaving(true);
                autoSaveTimerRef.current = setTimeout(() => {
                    autoSaveDraft(normalizedMilestones);
                }, 700);
            } else {
                // Invalid section: nothing will be saved, so it must not block the commit
                // (the commit is separately gated on every section being valid anyway).
                reportSaving(false);
            }
        }
    };

    const reportSaving = (isSaving) => {
        if (props.onAutosaveStateChange) {
            props.onAutosaveStateChange(props.section, isSaving);
        }
    };

    const autoSaveDraft = (normalizedMilestones) => {
        if (!props.postRoute) {
            reportSaving(false);
            return;
        }

        const data = {
            section: `fixed/${props.section}`,
            dates: normalizedMilestones.map((milestone) => ({
                ...milestone,
                perc: toFullPrecisionString(milestone.percentage ?? 0),
                value: toFullPrecisionString(milestone.value ?? 0),
            })),
        };

        axios
            .post(props.postRoute, data)
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                reportSaving(false);
            });
    };

    const handleFieldUpdate = (fieldName, value, setFieldValue, currentValues) => {
        // Show loading for many milestones
        if (currentValues?.dates?.length > 5) {
            setIsUpdating(true);
            setTimeout(() => {
                setFieldValue(fieldName, value);
                setIsUpdating(false);
            }, 100);
        } else {
            setFieldValue(fieldName, value);
        }
    };

    return (
        initialMilestones &&
        initialMilestones.length > 0 && (
            <div>
                <Formik
                    enableReinitialize
                    initialValues={{
                        dates: initialMilestones,
                        section: `fixed/${props.section}`,
                    }}
                    validationSchema={MilestoneSectionSchema}
                    onSubmit={(values, actions) => {
                        let data = {
                            ...values,
                            dates:
                                values.dates?.map((milestone) => {
                                    const { percOriginal, valueOriginal, ...rest } = milestone;
                                    const percSource = percOriginal ?? rest.perc ?? 0;
                                    const valueSource = valueOriginal ?? rest.value ?? 0;
                                    const percString = typeof percSource === 'string' ? percSource : toFullPrecisionString(percSource);
                                    const valueString = typeof valueSource === 'string' ? valueSource : toFullPrecisionString(valueSource);

                                    return {
                                        ...rest,
                                        perc: percString,
                                        value: valueString,
                                    };
                                }) ?? [],
                        };

                        axios
                            .post(props.postRoute, data)
                            .then((response) => {
                                actions.setSubmitting(false);
                                toast.success(props.parentSection === 'expense' ? $t(`${props.resource.name} cashflow updated`) : $t('Milestones confirmed'));
                                if (props.redirectRoute != '' && props.parentSection === 'expense') {
                                    redirectBack();
                                }
                                if (props.setMilestonesConfirmed) {
                                    if (props.section === 'cash' ? props.setCashMilestonesConfirmed : props.setVikMilestonesConfirmed) {
                                        props.section === 'cash' ? props.setCashMilestonesConfirmed(true) : props.setVikMilestonesConfirmed(true);
                                    }

                                    props.setMilestonesConfirmed(true);
                                    setIsConfirmed(true);
                                }
                            })
                            .catch((e) => {
                                actions.setSubmitting(false);

                                // Extract errors from response
                                const laravelErrors = e.response?.data?.errors || {};

                                // Transform Laravel's dot notation errors (dates.1.perc) to Formik's nested structure
                                const formikErrors = {};
                                if (Object.keys(laravelErrors).length > 0) {
                                    formikErrors.dates = [];
                                    Object.keys(laravelErrors).forEach((key) => {
                                        // Match pattern like "dates.1.perc" or "dates.0.date"
                                        const match = key.match(/^dates\.(\d+)\.(.+)$/);
                                        if (match) {
                                            const index = parseInt(match[1], 10);
                                            const field = match[2];

                                            // Ensure the array is large enough
                                            while (formikErrors.dates.length <= index) {
                                                formikErrors.dates.push(null);
                                            }

                                            // Initialize the object if it doesn't exist
                                            if (!formikErrors.dates[index]) {
                                                formikErrors.dates[index] = {};
                                            }

                                            // Clean up error messages by removing field path prefix (dates.X.field -> field)
                                            const fieldPathPattern = new RegExp(`dates\\.${index}\\.${field}`, 'g');
                                            const cleanedErrors = Array.isArray(laravelErrors[key])
                                                ? laravelErrors[key].map((msg) => {
                                                      if (typeof msg === 'string') {
                                                          // Replace "dates.X.field" with just "field" in the message
                                                          return msg.replace(fieldPathPattern, field);
                                                      }
                                                      return msg;
                                                  })
                                                : laravelErrors[key];

                                            // Set the field error
                                            formikErrors.dates[index][field] = cleanedErrors;
                                        }
                                    });
                                }

                                actions.setErrors(formikErrors);
                            });
                    }}
                >
                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => {
                        // The trailing column only ever holds the per-row delete button, which renders when there
                        // is more than one milestone and the row is deletable. Drop the column entirely otherwise
                        // so it isn't left as a permanently empty column (e.g. a single inherited milestone).
                        const showActionsColumn = (values.dates?.length || 0) > 1 && (values.dates || []).some((m) => m.canDeleteMilestones);

                        return (
                            <div className="tw-mt-4">
                                <FormObserver
                                    valuesListener={valuesListener}
                                    getMaxPercentage={getMaxPercentage}
                                    getUniqueKeyBasedOnIndex={getUniqueKeyBasedOnIndex}
                                    totalValue={totalValue}
                                    onMilestonesChange={handleMilestonesChange}
                                />
                                {/* Suppressed by the forms that render their own grey-caps header
                                    above this component, where this one is a duplicate — and reads
                                    "Payment" on the revenue and ticketing screens, which receive
                                    money. Keyed on an explicit prop rather than parentSection:
                                    PurchaseOrdersCreateForm also passes "direct-commit" and has no
                                    header of its own, so this is its only one. */}
                                <div className={props.hideSectionHeader ? 'tw-flex tw-justify-end tw-mb-4' : 'tw-flex tw-justify-between tw-mb-4'}>
                                    {!props.hideSectionHeader && (
                                        <h5 className="table__header">
                                            {props.sectionTitle} {$t('Payment Milestones')}
                                        </h5>
                                    )}
                                    <div className="tw-flex tw-items-center tw-space-x-1.5">
                                        <label className="tw-text-charcoal tw-text-sm tw-font-semibold tw-mb-0">{$t('Allocation')}:</label>
                                        <div className={' tw-flex tw-flex-row tw-items-center tw-gap-x-1'}>
                                            <select
                                                className="tw-border-0 select-input"
                                                value={allocationMode}
                                                onChange={(e) => {
                                                    const mode = e.target.value;
                                                    setAllocationMode(mode);
                                                    if (mode === 'auto') {
                                                        setShowAutoModal(true);
                                                    }
                                                }}
                                            >
                                                <option value="manual">{$t('Set Manually')}</option>
                                                <option value="auto">{$t('Allocate Automatically')}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-flex tw-flex-col tw-gap-y-2">
                                    <FieldArray
                                        enableReinitialize
                                        name="dates"
                                        render={(arrayHelpers) => (
                                            <>
                                                <div className="tw-mt-4 inbud-table_wrapper">
                                                    {isUpdating && (
                                                        <div className="tw-absolute tw-inset-0 tw-bg-white/50 tw-flex tw-items-center tw-justify-center tw-z-10">
                                                            <div className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-px-4 tw-py-2 tw-rounded-lg tw-shadow-lg">
                                                                <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-blue-600"></div>
                                                                <span className="tw-text-sm tw-text-gray-600">{$t('Updating...')}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <DragAndDrop
                                                        onDragEnd={(result) => {
                                                            const { type, source, destination } = result;

                                                            if (!destination) return;

                                                            const sourceIndex = source.index;
                                                            const destinationIndex = destination.index;

                                                            // Reordering milestones
                                                            if (type === 'droppable-milestone') {
                                                                arrayHelpers.move(sourceIndex, destinationIndex);
                                                            }
                                                        }}
                                                    >
                                                        <table className="tw-w-full inbud-table">
                                                            <thead>
                                                                <tr className="tw-bg-gray-50">
                                                                    <th className="tw-w-8 tw-p-3"></th>
                                                                    <th className="tw-w-1/4 tw-text-left tw-text-sm tw-font-medium tw-text-gray-700 tw-p-3">
                                                                        {$t('Date')}
                                                                    </th>
                                                                    <th className="tw-w-1/4 tw-text-left tw-text-sm tw-font-medium tw-text-gray-700 tw-p-3">
                                                                        {$t('Description')}
                                                                    </th>
                                                                    <th className="tw-w-1/4 tw-text-left tw-text-sm tw-font-medium tw-text-gray-700 tw-p-3">
                                                                        {$t('%')}
                                                                    </th>
                                                                    <th className="tw-w-1/4 tw-text-right tw-text-sm tw-font-medium tw-text-gray-700 tw-p-3">
                                                                        {$t('Value')}
                                                                    </th>
                                                                    {showActionsColumn && <th className="tw-w-8 tw-p-3"></th>}
                                                                </tr>
                                                            </thead>
                                                            <Droppable droppableId="milestones-droppable" type="droppable-milestone">
                                                                {(provided) => (
                                                                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                                        {values.dates?.map((milestone, index) => {
                                                                            const milestoneErrors = errors.dates && errors.dates[index];
                                                                            const milestoneTouched = touched.dates && touched.dates[index];

                                                                            return (
                                                                                <Draggable
                                                                                    key={`milestone-${index}`}
                                                                                    draggableId={`milestone-${index}`}
                                                                                    index={index}
                                                                                >
                                                                                    {(dragProvided) => (
                                                                                        <MilestoneRow
                                                                                            key={`milestone-${index}`}
                                                                                            index={index}
                                                                                            section={props.section}
                                                                                            minDate={props.minDate}
                                                                                            maxDate={props.maxDate}
                                                                                            values={values}
                                                                                            errors={milestoneErrors}
                                                                                            touched={milestoneTouched}
                                                                                            setFieldValue={setFieldValue}
                                                                                            totalValue={totalValue}
                                                                                            provided={dragProvided}
                                                                                            isSliderAllowed={isSliderAllowed}
                                                                                            handleFieldUpdate={handleFieldUpdate}
                                                                                            isUpdating={isUpdating}
                                                                                            showActions={showActionsColumn}
                                                                                            actions={
                                                                                                (values?.dates?.length || 0) > 1 &&
                                                                                                milestone.canDeleteMilestones && (
                                                                                                    <TextButton
                                                                                                        variant="danger"
                                                                                                        onClick={() => arrayHelpers.remove(index)}
                                                                                                    >
                                                                                                        <TrashIcon className="tw-w-3 tw-h-3" />
                                                                                                    </TextButton>
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </Draggable>
                                                                            );
                                                                        })}
                                                                        {provided.placeholder}
                                                                    </tbody>
                                                                )}
                                                            </Droppable>
                                                            <tfoot>
                                                                <tr className="tw-font-semibold">
                                                                    <td className="tw-p-3"></td>
                                                                    <td className="tw-p-3 tw-text-left">{$t('Total')}</td>
                                                                    <td className="tw-p-3"></td>
                                                                    <td className="tw-p-3 tw-text-left">
                                                                        {truncateDecimals(
                                                                            values.dates?.reduce((sum, milestone) => {
                                                                                const rawPerc = milestone.percOriginal ?? milestone.perc ?? 0;
                                                                                const perc = parseFloat(rawPerc);

                                                                                return sum + (isNaN(perc) ? 0 : perc);
                                                                            }, 0) ?? 0,
                                                                            2,
                                                                        ).toFixed(2)}
                                                                        %
                                                                    </td>
                                                                    <td className="tw-p-3 tw-text-right">
                                                                        {formatMoneyWithLocale(
                                                                            truncateDecimals(
                                                                                values.dates?.reduce((sum, milestone) => {
                                                                                    // Reconciled, not read raw: the footer must not show the total the
                                                                                    // rows have already moved past when the committing amount changes.
                                                                                    const perc = parseFloat(milestone.percOriginal ?? milestone.perc ?? 0);
                                                                                    const milestoneValue = reconcileMilestoneValue(
                                                                                        milestone.valueOriginal ?? milestone.value,
                                                                                        totalValue,
                                                                                        isNaN(perc) ? 0 : perc,
                                                                                    );
                                                                                    return sum + (isNaN(milestoneValue) ? 0 : milestoneValue);
                                                                                }, 0) ?? 0,
                                                                                2,
                                                                            ),
                                                                        )}
                                                                    </td>
                                                                    {showActionsColumn && <td className="tw-p-3"></td>}
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </DragAndDrop>
                                                </div>
                                                {hasAvailablePercentage && props.canEdit && (
                                                    <div className="tw-flex tw-flex-row tw-items-center tw-mt-3">
                                                        <Button variant="primary" small onClick={() => arrayHelpers.push(getDefaultMilestone(values))}>
                                                            {$t('Add Milestone')}
                                                        </Button>
                                                    </div>
                                                )}
                                                {showAutoModal && (
                                                    <AutoAllocationModal
                                                        showAutoModal={showAutoModal}
                                                        setShowAutoModal={setShowAutoModal}
                                                        setFieldValue={setFieldValue}
                                                        setAllocationMode={setAllocationMode}
                                                        section={props.section}
                                                        minDate={props.minDate}
                                                        maxDate={props.maxDate}
                                                        totalValue={totalValue}
                                                    />
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                                {!isSingleScreenCommit &&
                                    props.canEdit &&
                                    (props.parentSection === 'expense' || (props.parentSection !== 'expense' && !isConfirmed)) && (
                                        <div className="tw-flex tw-w-full tw-justify-end tw-gap-x-4 tw-my-4">
                                            {showCancelButton && (
                                                <Button variant="secondary" disabled={isSubmitting} onClick={redirectBack}>
                                                    {$t('Cancel')}
                                                </Button>
                                            )}

                                            <Tooltip
                                                placement="bottom"
                                                tooltipType="text"
                                                tooltipContent={$t('Please confirm and apply the above financial items first')}
                                                allowTooltip={!props.allowConfirm}
                                            >
                                                <span className="tw-inline-block">
                                                    <Button
                                                        variant="primary"
                                                        disabled={!isTotalPercentageValid || !isTotalValueValid || isSubmitting || !props.allowConfirm}
                                                        onClick={handleSubmit}
                                                    >
                                                        {props.submitButtonText}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                            </div>
                        );
                    }}
                </Formik>
            </div>
        )
    );
}
const FormObserver = (props) => {
    const { values, setFieldValue } = useFormikContext();
    const { getMaxPercentage, getUniqueKeyBasedOnIndex, totalValue, valuesListener, onMilestonesChange } = props;
    const valuesRef = useRef(values);
    valuesRef.current = values;

    // Depend only on user-visible changes: perc values, the editable date / description fields,
    // milestone count, and totalValue. Derived fields updated by this effect must NOT be deps —
    // they cause an infinite loop.
    //
    // Dates and descriptions belong here because the 700ms draft autosave is driven from this
    // effect: while they were absent, editing only a date never saved, and a second row appeared
    // to fix it purely because the row count IS watched and carried the date edit along.
    const percSignature = values.dates?.map((m) => m.percOriginal ?? m.perc).join(',') ?? '';
    const editSignature = values.dates?.map((m) => `${m.date ?? ''}|${m.name ?? ''}|${m.description ?? ''}`).join('~') ?? '';
    const datesLength = values.dates?.length ?? 0;

    useEffect(() => {
        const values = valuesRef.current;

        let totalPercentage = 0;
        let totalMilestoneValue = 0;
        const normalizedMilestones = [];
        values.dates?.forEach((milestone, index) => {
            const maxPerc = getMaxPercentage(values, index);
            const parseNumber = (val) => {
                const numeric = parseFloat(val);
                return isNaN(numeric) ? null : numeric;
            };

            let percOriginal = parseNumber(milestone?.percOriginal);
            const percDisplay = parseNumber(milestone?.perc);
            let valueOriginal = parseNumber(milestone?.valueOriginal);
            const valueDisplay = parseNumber(milestone?.value);

            if (percOriginal === null) {
                if (valueOriginal !== null && totalValue > 0) {
                    percOriginal = (valueOriginal / totalValue) * 100;
                } else if (valueDisplay !== null && totalValue > 0) {
                    percOriginal = (valueDisplay / totalValue) * 100;
                } else if (percDisplay !== null) {
                    percOriginal = percDisplay;
                } else {
                    percOriginal = 0;
                }
            }

            // Reconciled against totalValue x percOriginal on EVERY run, not only when it is
            // missing. The value is derived, so a figure that was right for the previous
            // committing amount is stale the instant that amount moves — and a guard that only
            // recomputed a null or zero value left exactly that stale figure in place, one
            // keystroke behind the hero. reconcileMilestoneValue keeps a value the user typed
            // (its percentage was back-computed from it, so the two already agree).
            valueOriginal = reconcileMilestoneValue(valueOriginal ?? valueDisplay, totalValue, percOriginal);

            const truncatedPerc = truncateDecimals(percOriginal, 2);
            const truncatedValue = truncateDecimals(valueOriginal, 2);
            const percOriginalString = toFullPrecisionString(percOriginal);
            const valueOriginalString = toFullPrecisionString(valueOriginal);

            if (milestone?.percOriginal !== percOriginalString) {
                setFieldValue(`dates.${index}.percOriginal`, percOriginalString);
            }
            if (milestone?.perc !== truncatedPerc) {
                setFieldValue(`dates.${index}.perc`, truncatedPerc);
            }
            if (milestone?.valueOriginal !== valueOriginalString) {
                setFieldValue(`dates.${index}.valueOriginal`, valueOriginalString);
            }
            if (milestone?.value !== truncatedValue) {
                setFieldValue(`dates.${index}.value`, truncatedValue);
            }

            totalPercentage += percOriginal;
            totalMilestoneValue += valueOriginal;
            if (milestone?.maxPercentage !== maxPerc) {
                setFieldValue(`dates.${index}.maxPercentage`, maxPerc);
            }
            const newUniqueKey = getUniqueKeyBasedOnIndex(values, index);
            if (milestone?.uniqueKey !== newUniqueKey) {
                setFieldValue(`dates.${index}.uniqueKey`, newUniqueKey);
            }

            // Push the updated milestone to the normalized milestones array
            normalizedMilestones.push({
                id: milestone?.id,
                name: milestone?.name ?? '',
                date: milestone?.date,
                description: milestone?.description ?? null,
                type: milestone?.type ?? CASH_MILESTONE_TYPE,
                canDeleteMilestones: milestone?.canDeleteMilestones,
                percentage: percOriginal,
                value: valueOriginal,
            });
        });
        valuesListener(totalPercentage, totalMilestoneValue);

        // Trigger the onMilestonesChange callback to update the milestones in the parent component
        if (onMilestonesChange && normalizedMilestones.length > 0) {
            onMilestonesChange(normalizedMilestones);
        }
    }, [percSignature, editSignature, datesLength, totalValue]);

    return null;
};
